import { collectionGroup, endBefore, getCountFromServer, getDocs, limit, limitToLast, query, startAfter, where, orderBy } from 'firebase/firestore';
import { toLower } from 'lodash';
import { isBooleanTrue, isListNotEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from '../utils/objectUtils';
import AuthUser from './AuthUser';
import { toE164PhoneNumber, toPhoneNumberLocal } from '../utils/stringFormatter';
import { db } from '../firebase';
import Address from './Address';
import { subCollectionCreate, subCollectionGetByParentIdAndSubId, subCollectionGetBySingleQuery, subCollectionRemove, subCollectionUpdate } from '../utils/dbUtils';

class PartnerUser {
	static collectionName = 'partners';
	static subCollectionName = 'partnerUsers';

	partnerId = null;
	userId = null;

	email = null;
	phone = null;

	firstName = null;
	firstNameLower = null;
	middleName = null;
	lastName = null;
	lastNameLower = null;
	suffix = null;

	billingAddress = new Address(null).toDto();
	shippingAddress = new Address(null).toDto();

	roles = ['ADMIN'];

	phoneVerified = false;

	constructor(obj) {
		if (isObjectNotEmpty(obj)) {
			if (isStringNotEmpty(obj.partnerId)) { this.partnerId = obj.partnerId; }
			if (isStringNotEmpty(obj.userId)) { this.userId = obj.userId; }

			if (isStringNotEmpty(obj.email)) { this.email = toLower(obj.email); }
			if (isStringNotEmpty(obj.phone)) { this.phone = toE164PhoneNumber(obj.phone); }

			if (isStringNotEmpty(obj.firstName)) {
				this.firstName = obj.firstName;
				this.firstNameLower = toLower(obj.firstName);
			}
			if (isStringNotEmpty(obj.lastName)) {
				this.lastName = obj.lastName;
				this.lastNameLower = toLower(obj.lastName);
			}
			if (isStringNotEmpty(obj.middleName)) { this.middleName = obj.middleName; }
			if (isStringNotEmpty(obj.suffix)) { this.suffix = obj.suffix; }

			if (isObjectNotEmpty(obj.billingAddress)) { this.billingAddress = new Address(obj.billingAddress).toDto(); }
			if (isObjectNotEmpty(obj.shippingAddress)) { this.shippingAddress = new Address(obj.shippingAddress).toDto(); }

			if (isListNotEmpty(obj.roles)) { this.roles = obj.roles; }

			if (isBooleanTrue(obj.phoneVerified)) { this.phoneVerified = true; }
		}
	}

	toDto = () => {
		return {
			partnerId: this.partnerId,
			userId: this.userId,

			email: this.email,
			phone: this.phone,

			firstName: this.firstName,
			firstNameLower: this.firstNameLower,
			middleName: this.middleName,
			lastName: this.lastName,
			lastNameLower: this.lastNameLower,
			suffix: this.suffix,

			billingAddress: this.billingAddress,
			shippingAddress: this.shippingAddress,

			roles: this.roles,

			phoneVerified: this.phoneVerified,
		};
	}

	isValid = () => {
		if (isStringEmpty(this.userId)) {
			throw new Error('userId is required.');
		}

		if (isStringEmpty(this.partnerId)) {
			throw new Error('partnerId is required.');
		}

		return true;
	}

	create = async () => {
		if (this.isValid()) {
			const existingPartnerUser = await PartnerUser.getByPartnerIdAndUserId(this.partnerId, this.userId);
			if (isObjectNotEmpty(existingPartnerUser)) {
				const success = await AuthUser.updatePartnerClaims(this.partnerId, this.userId, this.roles);
				if (isBooleanTrue(success)) {
					return { ...existingPartnerUser, roles: this.roles };
				}
			} else {
				const newPartnerUser = await subCollectionCreate(PartnerUser.collectionName, this.partnerId, PartnerUser.subCollectionName, this.userId, this.toDto());
				if (isObjectNotEmpty(newPartnerUser)) {
					await AuthUser.updatePartnerClaims(this.partnerId, this.userId, this.roles);

					return newPartnerUser;
				}
			}
		}

		return null;
	}

	static update = async (partnerId, userId, updatedPartnerUser) => {
		const updatedObj = new PartnerUser(updatedPartnerUser);
		if (updatedObj.isValid() && partnerId === updatedObj.partnerId && userId === updatedObj.userId) {
			const success = await subCollectionUpdate(PartnerUser.collectionName, updatedObj.partnerId, PartnerUser.subCollectionName, updatedObj.userId, updatedObj.toDto());
			if (isBooleanTrue(success)) {
				await AuthUser.updatePartnerClaims(updatedObj.partnerId, updatedObj.userId, updatedObj.roles);

				return true;
			}
		} else {
			throw new Error('partnerId and userId must match that of the updated partner user.')
		}

		return false;
	}

	static delete = async (partnerId, userId) => {
		return await subCollectionRemove(PartnerUser.collectionName, partnerId, PartnerUser.subCollectionName, userId);
	}

	static getByPartnerIdAndUserId = async (partnerId, userId) => {
		return await subCollectionGetByParentIdAndSubId(PartnerUser.collectionName, partnerId, PartnerUser.subCollectionName, userId);
	}

	static getByUserId = async (userId) => {
		return await subCollectionGetBySingleQuery(PartnerUser.subCollectionName, 'userId', userId, '==');
	}

	static getByQueryConstraints = async (queryConstraints = []) => {
		let q = query(collectionGroup(db, PartnerUser.subCollectionName), ...queryConstraints);

		const snapShot = await getDocs(q);
		// console.log(snapShot)
		return snapShot.docs.map((doc) => {
			return {
				id: doc.id, // append document id to each document
				...doc.data()
			};
		});
	}

	static getByPhone = async (phone) => {
		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(phone)) {
			const cleanPhone = toPhoneNumberLocal(phone);
			const formattedPhone = toE164PhoneNumber(phone);
			queryConstraints.push(where('phone', 'in', [phone, formattedPhone, cleanPhone]));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static getByEmail = async (email) => {
		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(email)) {
			queryConstraints.push(where('email', '==', toLower(email)));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static getByPartnerIdAndPhone = async (partnerId, phone) => {
		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(partnerId)) {
			queryConstraints.push(where('partnerId', '==', partnerId));
		}
		if (isStringNotEmptyOrZeroLength(phone)) {
			const cleanPhone = toPhoneNumberLocal(phone);
			const formattedPhone = toE164PhoneNumber(phone);
			queryConstraints.push(where('phone', 'in', [phone, formattedPhone, cleanPhone]));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static getByPartnerIdAndEmail = async (partnerId, email) => {
		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(partnerId)) {
			queryConstraints.push(where('partnerId', '==', partnerId));
		}
		if (isStringNotEmptyOrZeroLength(email)) {
			queryConstraints.push(where('email', '==', toLower(email)));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static getByPartnerIdAndFirstNameAndLastNameAndEmailAndPhone = async (partnerId, firstName, lastName, email, phone) => {
		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(partnerId)) {
			queryConstraints.push(where('partnerId', '==', partnerId));
		}
		if (isStringNotEmptyOrZeroLength(firstName)) {
			queryConstraints.push(where('firstNameLower', '==', toLower(firstName)));
		}
		if (isStringNotEmptyOrZeroLength(lastName)) {
			queryConstraints.push(where('lastNameLower', '==', toLower(lastName)));
		}
		if (isStringNotEmptyOrZeroLength(email)) {
			queryConstraints.push(where('email', '==', toLower(email)));
		}
		if (isStringNotEmptyOrZeroLength(phone)) {
			const cleanPhone = toPhoneNumberLocal(phone);
			const formattedPhone = toE164PhoneNumber(phone);
			queryConstraints.push(where('phone', 'in', [phone, formattedPhone, cleanPhone]));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static getCount = async (queryParameters) => {
		const { partnerId, role } = queryParameters;

		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(partnerId)) {
			queryConstraints.push(where('partnerId', '==', partnerId));
		}

		if (isListNotEmpty(role)) {
			queryConstraints.push(where('roles', 'array-contains-any', role));
		} else if (isStringNotEmptyOrZeroLength(role)) {
			queryConstraints.push(where('roles', 'array-contains', role));
		}


		let q = query(collectionGroup(db, PartnerUser.subCollectionName), ...queryConstraints);

		const snapShot = await getCountFromServer(q);
		//console.log(snapShot.data());

		return snapShot.data().count;
	}

	static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
		const { partnerId, role } = queryParameters;

		let queryConstraints = [];

		if (isListNotEmpty(partnerId)) {
			queryConstraints.push(where('partnerId', 'in', partnerId));
		} else if (isStringNotEmptyOrZeroLength(partnerId)) {
			queryConstraints.push(where('partnerId', '==', partnerId));
		}

		if (isListNotEmpty(role)) {
			queryConstraints.push(where('roles', 'array-contains-any', role));
		} else if (isStringNotEmptyOrZeroLength(role)) {
			queryConstraints.push(where('roles', 'array-contains', role));
		}

		queryConstraints.push(orderBy(orderByField, 'asc'));

		if (isStringNotEmpty(firstVisible)) {
			queryConstraints.push(limitToLast(pageSize));
			queryConstraints.push(endBefore(lastVisible[orderByField]));
		} else if (isStringNotEmpty(lastVisible)) {
			queryConstraints.push(limit(pageSize));
			queryConstraints.push(startAfter(lastVisible[orderByField]));
		} else {
			queryConstraints.push(limit(pageSize));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static get = async (queryParameters) => {
		let allRecords = [];
		try {
			let next = null;

			let totalCount = await this.getCount(queryParameters);
			let totalPages = Math.ceil(totalCount / 10000.0);
			// console.log('totalCount: ' + totalCount);
			// console.log('totalPages: ' + totalPages);
			let pages = [];
			for (let i = 0; i < totalPages; i++) {
				pages.push(i + 1);
			}
			for (const page of pages) {
				// console.log('starting page ' + page);

				let list = await this.getPaginated(queryParameters, 10000, null, next, 'createdAt');

				// console.log(list.length);
				allRecords = [...allRecords, ...list];

				// console.log(allRecords.length);

				// console.log('go to next page');
				next = list[list.length - 1];
			}
		} catch (error) {
			console.log(error);
		}

		return allRecords;
	}
}

export default PartnerUser;